import {AuthenticatedTemplate} from '@azure/msal-react';
import {Login} from '../../../Login';
import Hero from "../../common/layout/Hero";
import HeroBody from "../../common/layout/HeroBody";
import Container from "../../common/layout/Container";
import Columns from "../../common/layout/Columns";
import Column from "../../common/layout/Column";
import Select from "react-select";
import {useEffect, useState} from "react";
import "../css/insights.css"
import InfoCard from "../../common/elements/InfoCard";
import {ExperimentMetadata} from "../components/ExperimentMetadata";
import {ExperimentStats} from "../components/ExperimentStats";
import useFetchWithMsal from "../../../useFetchWithMsal";
import {thor_api_urls} from "../../common/Constants";
import SmallLoadingSpinner from "../../common/elements/SmallLoadingSpinner";
import {buildDataState, buildErrorState, buildLoadingState} from "../../../utils/StateUtils";
import StateHandler from "../../common/elements/StateHandler";
import {useSearchParams} from "react-router-dom";

export const RealTimeInsightsComponent = () => {
    const {execute} = useFetchWithMsal();

    const [searchParams, setSearchParams] = useSearchParams();

    const [experimentSelected, setExperimentSelected] = useState(undefined);

    const [experimentsMetadata, setExperimentsMetadata] = useState(buildLoadingState(new Map()))

    const [experimentLabels, setExperimentLabels] = useState([])

    function buildLoadingExperimentsScreen() {
        return <div className="button mt-4 mb-4 is-fullwidth">
            <span className="mt-4 mb-4 mr-4">Loading experiments..</span>
            <SmallLoadingSpinner/>
        </div>
    }

    const fetchExperiments = () => {
        const thorPromise = execute('GET', thor_api_urls.listAllExperiments)
            .then((data) => {
                if (!data || !data.experiments) return [];
                return data.experiments.map(exp => ({...exp, source: 'THOR'}));
            })
            .catch((error) => {
                console.log("Failed to fetch Thor experiments:", error);
                return [];
            });

        thorPromise
            .then((thorExperiments) => {
                let experiments = new Map();
                for (const experiment of thorExperiments) {
                    experiments.set(experiment.key, experiment);
                }
                setExperimentsMetadata(buildDataState(experiments));
                const experimentKeyInUrl = searchParams.get("experimentKey");
                if (experimentKeyInUrl && experiments.has(experimentKeyInUrl)) {
                    setExperimentSelected(experiments.get(experimentKeyInUrl));
                }
            })
            .catch((error) => {
                setTimeout(() => {
                    if (experimentsMetadata.loading) {
                        setExperimentsMetadata(buildErrorState(error));
                    }
                }, 1000);
                console.log(error);
            });
    }

    useEffect(() => {
        fetchExperiments()
    }, [execute])

    useEffect(() => {
        if (experimentSelected === null) {
            addParamToUrl('experimentKey', '');
        } else if (experimentSelected) {
            addParamToUrl('experimentKey', experimentSelected.key);
            if (experimentSelected.source !== 'EPPO') {
                fetchAudiencesForExperiment(experimentSelected.key, experimentSelected)
                fetchSegmentsForExperiment(experimentSelected.key, experimentSelected)
                fetchBucketsForExperiment(experimentSelected.key, experimentSelected)
                fetchInstrumentationInstructionsForExperiment(experimentSelected.key, experimentSelected)
            }
        }
    }, [experimentSelected])

    useEffect(() => {
        mapExperimentMetadataToLabels()
    }, [experimentsMetadata])

    function fetchAudiencesForExperiment(experimentKey, experiment) {
        experiment["experimentAudiences"] = []
        execute('GET', thor_api_urls.experimentAudiences(experimentKey))
            .then((data) => {
                if (!data || !data.experimentAudiences) {
                    return;
                }
                experiment["experimentAudiences"] = data.experimentAudiences
            })
            .catch((error) => {
                setTimeout(() => {
                    if(experimentsMetadata.loading) {
                        setExperimentsMetadata(buildErrorState(error))
                    }
                }, 1000)
                console.log(error);
            });
    }

    function fetchSegmentsForExperiment(experimentKey, experiment) {
        experiment["segments"] = []
        execute('GET', thor_api_urls.experimentSegments(experimentKey))
            .then((data) => {
                if (!data || !data.segments) {
                    return;
                }
                experiment["segments"] = data.segments
            })
            .catch((error) => {
                setTimeout(() => {
                    if(experimentsMetadata.loading) {
                        setExperimentsMetadata(buildErrorState(error))
                    }
                }, 1000)
                console.log(error);
            });
    }

    function fetchBucketsForExperiment(experimentKey, experiment) {
        experiment["buckets"] = []
        execute('GET', thor_api_urls.experimentBuckets(experimentKey))
            .then((data) => {
                if (!data || !data.buckets) {
                    return;
                }
                experiment["buckets"] = data.buckets
            })
            .catch((error) => {
                setTimeout(() => {
                    if(experimentsMetadata.loading) {
                        setExperimentsMetadata(buildErrorState(error))
                    }
                }, 1000)
                console.log(error);
            });
    }

    function fetchInstrumentationInstructionsForExperiment(experimentKey, experiment) {
        experiment["instrumentationInstructions"] = {}
        execute('GET', thor_api_urls.instrumentationInstructions(experimentKey))
            .then((data) => {
                if (!data) {
                    return;
                }
                experiment["instrumentationInstructions"] = data
            })
            .catch((error) => {
                setTimeout(() => {
                    if(experimentsMetadata.loading) {
                        setExperimentsMetadata(buildErrorState(error))
                    }
                }, 1000)
                console.log(error);
            });
    }

    function addParamToUrl(key, value) {
        const url = new URL(window.location.href);
        url.searchParams.set(key, value);
        window.history.replaceState(null, null, url); //prevents page reload
    }

    const mapExperimentMetadataToLabels = () => {
        let experimentsArray = []
        for (let [key, value] of experimentsMetadata.data) {
            const source = value["source"] || "THOR";
            experimentsArray.push({
                value: key,
                label: `${value["key"]} [${source}]`
            })
        }
        setExperimentLabels(experimentsArray.sort((a, b) => a.label.localeCompare(b.label)))
    }

    return (
        <>
            <Login/>
            <AuthenticatedTemplate>
                <Container>
                    <Hero>
                        <HeroBody>
                            <p className="title is-1">Real-Time Insights</p>
                        </HeroBody>
                    </Hero>

                    <StateHandler showLoader={experimentsMetadata.loading} showError={experimentsMetadata.error}
                                  loadingScreen={buildLoadingExperimentsScreen()}>
                        <Columns>
                            <Column className="is-full">
                                <Select
                                    className="basic-single"
                                    classNamePrefix="select"
                                    isClearable={true}
                                    isSearchable={true}
                                    defaultInputValue={experimentSelected?.key}
                                    name="color"
                                    options={experimentLabels}
                                    onChange={(selected) => {
                                        if (selected === null) {
                                            setExperimentSelected(null);
                                            return;
                                        }
                                        setExperimentSelected(experimentsMetadata.data?.get(selected.value));
                                    }}
                                    placeholder={"Type your experiment key"}
                                />
                            </Column>
                        </Columns>

                        {!experimentSelected ?
                            <InfoCard title={"You need to select an experiment."}/>
                            :
                            <>
                                <ExperimentMetadata experiment={experimentSelected}/>
                                <br/>
                                <ExperimentStats experimentKey={experimentSelected.key}/>
                            </>
                        }
                    </StateHandler>
                </Container>
            </AuthenticatedTemplate>
        </>
    );
}